<template>
  <span>
    <p>
      <translate data-po-exclude="true" :key="metricTitle">{{ metricTitle }}</translate>
      <el-tooltip class="item" effect="dark" placement="top" v-show="tooltipText">
        <div slot="content"><translate v-for="text in tooltipText" :key="text" data-po-exclude="true"> {{ text }}<br /> </translate></div>
        <span :class="labelClasses">
          <translate :key="displayMeasure" data-po-exclude="true">{{ displayMeasure }}</translate>
          <translate :key="unitOfMeasure" data-po-exclude="true">{{ unitOfMeasure }}</translate></span>
      </el-tooltip>
    </p>
    <slot id="progressBar"></slot>
  </span>
</template>

<script>
  /**
  * Just an empty component for quick copy and pasting purposes
  * Feel free to ignore
  */

  import { evaluate } from '@/../../shared/src/utils'
  import { markForTranslation as _ } from '@/../../i18n/scripts/utils'

  export default {
    name: 'QualityReportMetric',
    data () {
      return {
        'packetLoss': {
          'title': {
            'in-progress': _('Total Packet Loss'),
            'complete': _('% Packet Loss')
          },
          'unit': {
            'in-progress': _('pkts'),
            'complete': _('%')
          },
          'explainer': {
            'in-progress': [_('The amount of data lost between your device and our servers. The lower the better')],
            'complete': [_('The % of data lost between your device and our servers. The lower the better')]
          }
        },
        'packets': {
          'title': {
            'in-progress': _('Total Bytes Received'),
            'complete': _('% Bytes Received')
          },
          'unit': {
            'in-progress': _('pkts'),
            'complete': _('%')
          },
          'explainer': {
            'in-progress': [_('The amount of data sent from our servers and received by you. '), _('This is measured in packets.')],
            'complete': [_('The % of data sent from our servers and received by you. This should be over 98% for a successful class.')]
          }
        },
        'latency': {
          'title': {
            'in-progress': _('Network Delay'),
            'complete': _('Average Network Delay')
          },
          'unit': {
            'in-progress': _('ms'),
            'complete': _('ms')
          },
          'explainer': {
            'in-progress': [_('The time it takes to send data between your device and our servers in milliseconds')],
            'complete': [_('The average time it took to send data between your device and our servers in milliseconds')]
          }
        },
        'jitter': {
          'title': {
            'in-progress': _('Packet Jitter'),
            'complete': _('Average Packet Jitter')
          },
          'unit': {
            'in-progress': _('ms'),
            'complete': _('ms')
          },
          'explainer': {
            'in-progress': [_('The delay between receiving data packets from your device')],
            'complete': [_('The average delay between data packets received from your device')]
          }
        },
        'frameRate': {
          'title': {
            'in-progress': _('Picture Quality'),
            'complete': _('Average Picture Quality')
          },
          'unit': {
            'in-progress': _('fps'),
            'complete': _('fps')
          },
          'explainer': {
            'in-progress': [_(' The quality of your video display ')],
            'complete': [_(' The frame rate is the number of frames per second. Higher is better. 0 indicates a problem.')]
          }
        },
        'audioLevel': {
          'title': {
            'in-progress': _('Max Volume'),
            // 'in-progress': _('Volume'),
            'complete': _('Max Volume')
          },
          'unit': {
            'in-progress': '',
            'complete': ''
          },
          'explainer': {
            'in-progress': [_('The maximum volume detected from your microphone. Higher is better. 0 indicates a problem.')],
            // 'in-progress': [_('The current volume detected from your microphone')],
            'complete': [_('The maximum volume detected from your microphone. Higher is better. 0 indicates a problem.')]
          }
        }
      }
    },

    props: {

      reportType: {
        'type': String,
        'default': ''
      },

      metric: {
        'type': String,
        'default': ''
      },

      progress: {
        'type': Number,
        'default': 0
      },

      measure: {
        'type': Number,
        'default': 0
      },

      aggregate: {
        'type': Number,
        'default': 0
      },

      threshold: {
        'type': Number,
        'default': 0
      },

      operator: {
        'type': String,
        'default': 'lt'
      }
    },

    computed: {

      inProgress () {
        return this.progress < 100
      },

      status () {
        if (this.progress === 100) {
          return 'complete'
        }
        return 'in-progress'
      },

      metricTitle () {
        return this[this.metric]['title'][this.status]
      },

      unitOfMeasure () {
        return this[this.metric]['unit'][this.status]
      },

      tooltipText () {
        return this[this.metric]['explainer'][this.status]
      },

      displayMeasure () {
        return this.inProgress ? this.measure : this.aggregate
      },

      success () {
        return evaluate(this.aggregate, this.operator, this.threshold)
      },

      wrapperClasses () {
        return {
          'progress': true,
          'progress-sm': true,
          'progress-striped': this.inProgress,
          'active': this.inProgress
        }
      },

      progressClasses () {
        return {
          'progress-bar': true,
          'progress-bar-primary': this.inProgress,
          'progress-bar-success': !this.inProgress && this.success,
          'progress-bar-danger': !this.inProgress && !this.success
        }
      },

      labelClasses () {
        return {
          'label': true,
          'pull-right': true,
          'label-primary': this.inProgress,
          'label-success': !this.inProgress && this.success,
          'label-danger': !this.inProgress && !this.success
        }
      },

      progressWidth () {
        return {
          'width': `${this.progress}%`
        }
      }
    },

    methods: {

      report () {

        const qualifier = this.success ? '' : ' not '
        const desc = this.operator === 'lt' ? 'less than ' : 'greater than'
        const limit = this.operator === 'lt' ? 'allowed' : 'required'

        return {
          'success': this.success,
          'reportType': this.reportType,
          'metric': this.metric,
          'message': `${this.metric} (${this.aggregate}) is ${qualifier} ${desc} ${limit} (${this.threshold}))`
        }
      }
    }
  }
</script>

<style scoped>
  p{
    text-align:left
  }
</style>
